import React, { useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import {
  ROLE_SUMMARY_ACCESS_LEVELS,
  ROLE_SUMMARY_PAGES,
  ROLE_SUMMARY_REVIEWED_ON,
  ROLE_SUMMARY_ROLES,
  ROLE_SUMMARY_SECTIONS,
} from "./RoleSummaryData";

const ALL_ROLES = "all";
const ALL_SECTIONS = "all";
const SECTION_END_BORDER = "3px solid #b8c2cc";
const stickySectionStyle = {
  backgroundColor: "#ffffff",
  left: 0,
  minWidth: 125,
  position: "sticky",
  width: 125,
  zIndex: 2,
};
const stickyPageStyle = {
  backgroundColor: "#ffffff",
  boxShadow: "2px 0 3px rgba(0, 0, 0, 0.06)",
  left: 125,
  minWidth: 185,
  position: "sticky",
  width: 185,
  zIndex: 2,
};

const normalizeSearchText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const RoleAccessCell = ({ permission }) => {
  const level =
    ROLE_SUMMARY_ACCESS_LEVELS[permission.level] ||
    ROLE_SUMMARY_ACCESS_LEVELS.none;

  return (
    <div style={{ minWidth: 170, whiteSpace: "normal" }}>
      <Badge variant={level.variant}>{permission.label || level.label}</Badge>
      <div className="small mt-1" style={{ lineHeight: 1.35 }}>
        {permission.detail}
      </div>
    </div>
  );
};

const RoleSummary = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState(ALL_ROLES);
  const [selectedSection, setSelectedSection] = useState(ALL_SECTIONS);

  const visibleRoles = useMemo(
    () =>
      selectedRole === ALL_ROLES
        ? ROLE_SUMMARY_ROLES
        : ROLE_SUMMARY_ROLES.filter((role) => role.key === selectedRole),
    [selectedRole],
  );

  const filteredPages = useMemo(() => {
    const searchKey = normalizeSearchText(searchText);

    return ROLE_SUMMARY_PAGES.filter((item) => {
      const matchesSection =
        selectedSection === ALL_SECTIONS || item.section === selectedSection;
      const matchesSearch =
        !searchKey ||
        [item.section, item.page, item.path].some((value) =>
          normalizeSearchText(value).includes(searchKey),
        );

      return matchesSection && matchesSearch;
    });
  }, [searchText, selectedSection]);

  const clearFilters = () => {
    setSearchText("");
    setSelectedRole(ALL_ROLES);
    setSelectedSection(ALL_SECTIONS);
  };

  return (
    <React.Fragment>
      <PageTitle
        activeMenu="Summarize Role"
        motherMenu="Admin"
        motherLink="/"
      />

      <div className="mb-4">
        <h3 className="mb-1">Role and Page Access</h3>
        <p className="text-muted mb-0">
          Check which pages each role normally uses, what data they see, and
          which actions are limited.
        </p>
        <small className="text-muted">
          Last reviewed: <time dateTime={ROLE_SUMMARY_REVIEWED_ON}>{ROLE_SUMMARY_REVIEWED_ON}</time>
        </small>
      </div>

      <div className="alert alert-light border mb-4" role="note">
        <div className="font-weight-bold mb-2">How to read this page</div>
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          {Object.keys(ROLE_SUMMARY_ACCESS_LEVELS).map((levelKey) => {
            const level = ROLE_SUMMARY_ACCESS_LEVELS[levelKey];
            return (
              <Badge key={levelKey} variant={level.variant} className="p-2">
                {level.label}
              </Badge>
            );
          })}
        </div>
        <div className="small text-muted mt-2">
          This table describes normal menu and workflow access. A hidden menu
          does not always block a direct link. Node/customer assignments,
          payment access, and email grants can further limit an account's access.
          Each column describes a single role; accounts with multiple roles may
          behave differently.
        </div>
      </div>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-end">
            <Col xl={5} lg={5} md={12} className="mb-3 mb-lg-0">
              <Form.Group className="mb-0" controlId="role-summary-search">
                <Form.Label className="font-weight-bold">Search page</Form.Label>
                <Form.Control
                  type="search"
                  value={searchText}
                  placeholder="Search by page, section, or route"
                  aria-label="Search pages"
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xl={3} lg={3} md={6} className="mb-3 mb-lg-0">
              <Form.Group className="mb-0" controlId="role-summary-role-filter">
                <Form.Label className="font-weight-bold">Show role</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedRole}
                  aria-label="Show role"
                  onChange={(event) => setSelectedRole(event.target.value)}
                >
                  <option value={ALL_ROLES}>All roles</option>
                  {ROLE_SUMMARY_ROLES.map((role) => (
                    <option key={role.key} value={role.key}>
                      {role.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col xl={3} lg={3} md={6} className="mb-3 mb-lg-0">
              <Form.Group
                className="mb-0"
                controlId="role-summary-section-filter"
              >
                <Form.Label className="font-weight-bold">Show section</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedSection}
                  aria-label="Show section"
                  onChange={(event) => setSelectedSection(event.target.value)}
                >
                  <option value={ALL_SECTIONS}>All sections</option>
                  {ROLE_SUMMARY_SECTIONS.map((section) => (
                    <option key={section} value={section}>
                      {section}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col xl={1} lg={1} md={12}>
              <Button
                type="button"
                variant="outline-secondary"
                className="w-100 text-nowrap"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">Page permissions</h4>
            <small className="text-muted">
              Showing {filteredPages.length} of {ROLE_SUMMARY_PAGES.length} pages
            </small>
          </div>
          {selectedRole !== ALL_ROLES && (
            <Badge variant="primary" className="p-2">
              {visibleRoles[0] && visibleRoles[0].label}
            </Badge>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {filteredPages.length > 0 ? (
            <div className="table-responsive">
              <Table
                bordered
                hover
                size="sm"
                className="mb-0"
                aria-label="Role and page access matrix"
                style={{
                  minWidth: 310 + visibleRoles.length * 190,
                  tableLayout: "fixed",
                }}
              >
                <thead className="thead-light">
                  <tr>
                    <th
                      style={{
                        ...stickySectionStyle,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                      }}
                    >
                      Section
                    </th>
                    <th
                      style={{
                        ...stickyPageStyle,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                      }}
                    >
                      Page
                    </th>
                    {visibleRoles.map((role) => (
                      <th key={role.key} style={{ width: 190 }}>
                        {role.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.map((item, index) => {
                    const nextItem = filteredPages[index + 1];
                    const isSectionEnd =
                      !nextItem || nextItem.section !== item.section;
                    const sectionEndStyle = isSectionEnd
                      ? { borderBottom: SECTION_END_BORDER }
                      : {};

                    return (
                      <tr key={item.id}>
                        <td
                          className="align-top"
                          style={{
                            ...stickySectionStyle,
                            ...sectionEndStyle,
                          }}
                        >
                          <Badge variant="light" className="border">
                            {item.section}
                          </Badge>
                        </td>
                        <td
                          className="align-top"
                          style={{
                            ...stickyPageStyle,
                            ...sectionEndStyle,
                            whiteSpace: "normal",
                          }}
                        >
                          <div className="font-weight-bold">{item.page}</div>
                          <code className="small text-muted">{item.path}</code>
                        </td>
                        {visibleRoles.map((role) => (
                          <td
                            key={role.key}
                            className="align-top"
                            style={sectionEndStyle}
                          >
                            <RoleAccessCell permission={item.access[role.key]} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center text-muted p-5">
              No pages match the selected filters.
            </div>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default RoleSummary;
